/**
 * Categories Grid Section
 * Industry-specific category layouts
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useDarkModeNeutrals from '../../hooks/useDarkModeNeutrals';
import useStorefrontTheme from '../../hooks/useStorefrontTheme';
import './sections.css';

function CategoriesGrid({ settings = {}, content = {}, globalStyles = {}, store, industry }) {
  // Dark-mode-aware neutrals — see FeaturedProducts for the same
  // pattern. Brand-driven values (overlayColor stays #000) are left
  // hardcoded.
  const neutrals = useDarkModeNeutrals();
  const {
    columns = 4,
    showCount = true,
    imageStyle = 'square', // square, circle, portrait
    variant = 'default',
    // Granular style settings
    backgroundColor: configuredBackgroundColor,
    titleColor: configuredTitleColor,
    categoryNameColor: configuredCategoryNameColor,
    countColor: configuredCountColor,
    overlayColor = '#000000',
    overlayOpacity = 0.3,
    cardRadius = '12px',
    hoverEffect = 'zoom',
    darkBackgroundColor,
    darkTitleColor,
    darkCategoryNameColor,
    darkCountColor,
  } = settings;

  const { resolvedTheme } = useStorefrontTheme();
  const isDark = resolvedTheme === 'dark';
  const backgroundColor = (isDark ? darkBackgroundColor : configuredBackgroundColor) || neutrals.background;
  const titleColor = (isDark ? darkTitleColor : configuredTitleColor) || neutrals.text;
  const categoryNameColor = (isDark ? darkCategoryNameColor : configuredCategoryNameColor) || neutrals.text;
  const countColor = (isDark ? darkCountColor : configuredCountColor) || neutrals.textMuted;

  const {
    title = 'Shop by Category',
    subtitle = '',
    categoryIds = [],
  } = content;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const colors = globalStyles.colors || {};
  const fonts = globalStyles.fonts || {};

  // Style settings object to pass down
  const styleSettings = {
    categoryNameColor,
    countColor,
    overlayColor,
    overlayOpacity,
    cardRadius,
    hoverEffect,
  };

  // Determine effective variant based on industry
  const effectiveVariant = variant !== 'default' ? variant : getIndustryVariant(industry);

  // Depend on stable primitives only. `store` (object) and `categoryIds`
   // (array default to []) used to be new references every render, which
   // refired this effect in a loop. Using store?.slug + a joined key for
   // the array fixes it.
  const categoryIdsKey = categoryIds.join(',');
  useEffect(() => {
    const fetchCategories = async () => {
      if (!store?.id && !store?.slug) {
        setLoading(false);
        return;
      }

      try {
        const url = `/api/stores/${store.slug}/categories`;
        const res = await fetch(url);

        if (!res.ok) {
          setLoading(false);
          return;
        }

        const data = await res.json();

        if (data.success && data.categories) {
          const cats = categoryIds.length > 0
            ? data.categories.filter(c => categoryIds.includes(c.id))
            : data.categories;
          setCategories(cats);
        }
      } catch (err) {
        // Silently fail - categories section will show empty state
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.slug, categoryIdsKey]);

  const gridStyle = {
    '--columns': columns,
    '--primary-color': colors.primary || '#2563eb',
    '--text-color': categoryNameColor,
    '--hover-effect': hoverEffect,
    backgroundColor: backgroundColor,
  };

  const titleStyle = {
    fontFamily: fonts.heading || 'inherit',
    color: titleColor,
  };

  if (loading) {
    return (
      <section className={`ts-categories-grid ts-categories-grid--${effectiveVariant}`}>
        <div className="ts-categories-grid__container">
          <div className="ts-categories-grid__loading">
            <div className="ts-categories-grid__spinner" />
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return (
      <section className={`ts-categories-grid ts-categories-grid--${effectiveVariant}`}>
        <div className="ts-categories-grid__container">
          <div className="ts-categories-grid__header">
            <h2 className="ts-categories-grid__title" style={titleStyle}>{title}</h2>
          </div>
          <div className="ts-categories-grid__empty">
            <p>No categories available</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`ts-categories-grid ts-categories-grid--${effectiveVariant}`} style={gridStyle}>
      <div className="ts-categories-grid__container">
        {title && (
          <div className="ts-categories-grid__header">
            <h2 className="ts-categories-grid__title" style={titleStyle}>
              {title}
            </h2>
            {subtitle && (
              <p className="ts-categories-grid__subtitle">{subtitle}</p>
            )}
          </div>
        )}

        <div className="ts-categories-grid__grid">
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              variant={effectiveVariant}
              imageStyle={imageStyle}
              showCount={showCount}
              colors={colors}
              styleSettings={styleSettings}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function getIndustryVariant(industry) {
  const industryMap = {
    FASHION: 'editorial',
    FMCG: 'icon',
    TECH: 'dark',
    RESTAURANT: 'menu',
    LUXURY: 'minimal',
    KIDS: 'playful',
    AUTOS: 'dark',
    REAL_ESTATE: 'minimal',
    HOME_SERVICES: 'default',
    BEAUTY: 'minimal',
    SERVICES: 'default',
    GENERAL: 'default',
  };
  return industryMap[industry] || 'default';
}

function CategoryCard({ category, variant, imageStyle, showCount, colors, styleSettings = {} }) {
  switch (variant) {
    case 'editorial':
      return <EditorialCategoryCard category={category} showCount={showCount} styleSettings={styleSettings} />;
    case 'icon':
      return <IconCategoryCard category={category} showCount={showCount} colors={colors} styleSettings={styleSettings} />;
    case 'dark':
      return <DarkCategoryCard category={category} showCount={showCount} colors={colors} styleSettings={styleSettings} />;
    case 'minimal':
      return <MinimalCategoryCard category={category} showCount={showCount} styleSettings={styleSettings} />;
    case 'playful':
      return <PlayfulCategoryCard category={category} showCount={showCount} colors={colors} styleSettings={styleSettings} />;
    default:
      return <DefaultCategoryCard category={category} imageStyle={imageStyle} showCount={showCount} styleSettings={styleSettings} />;
  }
}

// Default card with overlay
function DefaultCategoryCard({ category, imageStyle, showCount, styleSettings = {} }) {
  const { categoryNameColor, countColor, overlayColor, overlayOpacity, cardRadius, hoverEffect } = styleSettings;

  // Parse overlay color to RGB for opacity
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '#000000');
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
  };

  const cardStyle = {
    borderRadius: cardRadius,
    '--hover-effect': hoverEffect,
  };

  const overlayStyle = {
    backgroundColor: `rgba(${hexToRgb(overlayColor)}, ${overlayOpacity})`,
  };

  return (
    <Link
      to={`/category/${category.slug || category.id}`}
      className={`ts-category-card ts-category-card--${imageStyle} ts-category-card--hover-${hoverEffect}`}
      style={cardStyle}
    >
      <div className="ts-category-card__image-wrapper">
        {category.image ? (
          <img
            src={category.image}
            alt={category.name}
            className="ts-category-card__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-category-card__placeholder">
            <svg width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
        )}
        <div className="ts-category-card__overlay" style={overlayStyle} />
      </div>
      <div className="ts-category-card__info">
        <h3 className="ts-category-card__name" style={{ color: categoryNameColor }}>{category.name}</h3>
        {showCount && category.productCount !== undefined && (
          <span className="ts-category-card__count" style={{ color: countColor }}>
            {category.productCount} {category.productCount === 1 ? 'item' : 'items'}
          </span>
        )}
      </div>
    </Link>
  );
}

// Fashion editorial style - large text, minimal
function EditorialCategoryCard({ category, showCount, styleSettings = {} }) {
  const { categoryNameColor, overlayColor, overlayOpacity, cardRadius, hoverEffect } = styleSettings;

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '#000000');
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
  };

  const cardStyle = { borderRadius: cardRadius };
  const overlayStyle = { backgroundColor: `rgba(${hexToRgb(overlayColor)}, ${overlayOpacity})` };

  return (
    <Link
      to={`/category/${category.slug || category.id}`}
      className={`ts-category-card ts-category-card--editorial ts-category-card--hover-${hoverEffect}`}
      style={cardStyle}
    >
      <div className="ts-editorial-cat__image-wrapper">
        {category.image ? (
          <img
            src={category.image}
            alt={category.name}
            className="ts-editorial-cat__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-editorial-cat__placeholder" />
        )}
        <div className="ts-editorial-cat__overlay" style={overlayStyle} />
      </div>
      <div className="ts-editorial-cat__content">
        <h3 className="ts-editorial-cat__name" style={{ color: categoryNameColor }}>{category.name}</h3>
        <span className="ts-editorial-cat__cta">Shop Now</span>
      </div>
    </Link>
  );
}

// FMCG icon-style - circle icons
function IconCategoryCard({ category, showCount, colors, styleSettings = {} }) {
  const { categoryNameColor, countColor, hoverEffect } = styleSettings;

  return (
    <Link
      to={`/category/${category.slug || category.id}`}
      className={`ts-category-card ts-category-card--icon ts-category-card--hover-${hoverEffect}`}
    >
      <div className="ts-icon-cat__circle" style={{ borderColor: colors.primary }}>
        {category.image ? (
          <img
            src={category.image}
            alt={category.name}
            className="ts-icon-cat__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-icon-cat__placeholder">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path d="M4 7h16M4 12h16m-7 5h7" />
            </svg>
          </div>
        )}
      </div>
      <h3 className="ts-icon-cat__name" style={{ color: categoryNameColor }}>{category.name}</h3>
      {showCount && category.productCount !== undefined && (
        <span className="ts-icon-cat__count" style={{ color: countColor }}>{category.productCount}</span>
      )}
    </Link>
  );
}

// Tech dark style
function DarkCategoryCard({ category, showCount, colors, styleSettings = {} }) {
  const { categoryNameColor, countColor, cardRadius, hoverEffect } = styleSettings;

  const cardStyle = { borderRadius: cardRadius };

  return (
    <Link
      to={`/category/${category.slug || category.id}`}
      className={`ts-category-card ts-category-card--dark ts-category-card--hover-${hoverEffect}`}
      style={cardStyle}
    >
      <div className="ts-dark-cat__image-wrapper">
        {category.image ? (
          <img
            src={category.image}
            alt={category.name}
            className="ts-dark-cat__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-dark-cat__placeholder">
            <svg width="40" height="40" fill="none" stroke={colors.accent || '#00ff88'} strokeWidth="1.5" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2" />
            </svg>
          </div>
        )}
      </div>
      <div className="ts-dark-cat__info">
        <h3 className="ts-dark-cat__name" style={{ color: categoryNameColor }}>{category.name}</h3>
        {showCount && category.productCount !== undefined && (
          <span className="ts-dark-cat__count" style={{ color: countColor || colors.accent }}>
            {category.productCount} products
          </span>
        )}
      </div>
    </Link>
  );
}

// Luxury minimal style
function MinimalCategoryCard({ category, showCount, styleSettings = {} }) {
  const { categoryNameColor, cardRadius, hoverEffect } = styleSettings;

  const cardStyle = { borderRadius: cardRadius };

  return (
    <Link
      to={`/category/${category.slug || category.id}`}
      className={`ts-category-card ts-category-card--minimal ts-category-card--hover-${hoverEffect}`}
      style={cardStyle}
    >
      <div className="ts-minimal-cat__image-wrapper">
        {category.image ? (
          <img
            src={category.image}
            alt={category.name}
            className="ts-minimal-cat__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-minimal-cat__placeholder" />
        )}
      </div>
      <div className="ts-minimal-cat__info">
        <h3 className="ts-minimal-cat__name" style={{ color: categoryNameColor }}>{category.name}</h3>
        <span className="ts-minimal-cat__arrow">
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

// Kids playful style
function PlayfulCategoryCard({ category, showCount, colors, styleSettings = {} }) {
  const { categoryNameColor, cardRadius, hoverEffect } = styleSettings;

  return (
    <Link
      to={`/category/${category.slug || category.id}`}
      className={`ts-category-card ts-category-card--playful-cat ts-category-card--hover-${hoverEffect}`}
    >
      <div className="ts-playful-cat__bubble" style={{ backgroundColor: colors.primary + '20', borderRadius: cardRadius }}>
        {category.image ? (
          <img
            src={category.image}
            alt={category.name}
            className="ts-playful-cat__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-playful-cat__emoji">
            {getCategoryEmoji(category.name)}
          </div>
        )}
      </div>
      <h3 className="ts-playful-cat__name" style={{ color: categoryNameColor }}>{category.name}</h3>
    </Link>
  );
}

function getCategoryEmoji(name) {
  const emojiMap = {
    toys: '🧸',
    clothes: '👕',
    shoes: '👟',
    books: '📚',
    food: '🍼',
    default: '✨',
  };
  const lower = name.toLowerCase();
  return emojiMap[lower] || emojiMap.default;
}

export default React.memo(CategoriesGrid);
